import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './CorrectionModal.css';
import ValidationWarningModal from './ValidationWarningModal';
import { auth } from '../../services/api';

interface Field {
  fieldName: string;
  displayName: string;
  mapping: string | null;
  fieldType: string;
  isMapped: boolean;
  isCalculated: boolean;
  isVisual: boolean;
  isPlaceholder: boolean;
  isPerSite?: boolean;
  extractedValue: any;
  correctedValue: any;
  value: string;
}

interface Section {
  label: string;
  fields: Field[];
  isPerSite?: boolean;
  display_order?: number;
}

interface FieldsBySection {
  [key: string]: Section;
}

type InvoiceShape = 'distribution' | 'supplier';

interface CorrectionModalProps {
  invoiceId: number;
  utilityType: string;
  onClose: () => void;
  onSave: (correctedData: any) => void;
  fromDuplicateQueue?: boolean;
}

const CorrectionModal: React.FC<CorrectionModalProps> = ({ invoiceId, utilityType, onClose, onSave, fromDuplicateQueue = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [fieldsBySection, setFieldsBySection] = useState<FieldsBySection | null>(null);
  const [activeSection, setActiveSection] = useState<string>('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [showPdf, setShowPdf] = useState(true);
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  const [emptyFieldsCount, setEmptyFieldsCount] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [savingFromEdit, setSavingFromEdit] = useState(false);
  const [sectionsWithEmptyFields, setSectionsWithEmptyFields] = useState<Set<string>>(new Set());
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

  // Supplier-shape state (unused for distribution invoices).
  // shape='distribution' keeps the legacy flat fieldValues flow untouched.
  // shape='supplier' splits state into masterValues + siteValues[currentSiteIndex],
  // with pagination across sites for per-site sections.
  const [shape, setShape] = useState<InvoiceShape>('distribution');
  const [masterValues, setMasterValues] = useState<Record<string, string>>({});
  const [siteValues, setSiteValues] = useState<Record<string, string>[]>([]);
  const [currentSiteIndex, setCurrentSiteIndex] = useState<number>(0);

  // Adjustment indicator.
  //
  // An adjustment-only bill (a retroactive credit with no distribution section) is handled
  // very differently from an ordinary invoice, and today a reviewer can only tell by reading
  // the charge fields. Two independent signals are available, and either is sufficient:
  //
  //   1. `Is Adjustment Invoice` - the model's explicit classification. It lives in `master`
  //      rather than `allFields` on purpose: it is requested via an extraction instruction so
  //      it never becomes a required field or a row in the correction grid.
  //   2. an `adj-` prefixed per-account `Account No` - written by the processor's deterministic
  //      normalizer after property enrichment.
  //
  // Signal 1 appears as soon as the invoice is extracted; signal 2 only after normalization has
  // run. Checking both means the badge is right whether or not normalization has happened yet,
  // and it still works on an invoice normalized before the flag existed.
  const isAdjustment = useMemo(() => {
    // `unknown`: masterValues is typed Record<string,string>, but the raw master payload can
    // carry a real boolean, and both forms must be accepted.
    const flag: unknown = masterValues['Is Adjustment Invoice'] ?? invoiceData?.master?.['Is Adjustment Invoice'];
    if (typeof flag === 'string' && ['true', 'yes', 'y', '1'].includes(flag.trim().toLowerCase())) return true;
    if (flag === true) return true;
    const prefixed = (values: Record<string, string>[] | undefined) =>
      (values || []).some(site => String(site?.['Account No'] ?? '').startsWith('adj-'));
    return prefixed(siteValues) || prefixed(invoiceData?.accounts);
  }, [masterValues, siteValues, invoiceData]);

  // Read the current display value for a field, branching on invoice shape.
  // For supplier per-site sections, the value comes from the currently-paginated site.
  const getFieldValue = useCallback((fieldName: string, isPerSite: boolean): string => {
    if (shape === 'supplier') {
      if (isPerSite) {
        return siteValues[currentSiteIndex]?.[fieldName] ?? '';
      }
      return masterValues[fieldName] ?? '';
    }
    return fieldValues[fieldName] ?? '';
  }, [shape, siteValues, currentSiteIndex, masterValues, fieldValues]);

  const checkSectionsWithEmptyFields = useCallback(() => {
    const sectionsWithEmpty = new Set<string>();

    if (fieldsBySection) {
      Object.entries(fieldsBySection).forEach(([sectionKey, section]) => {
        const hasEmptyFields = section.fields.some(field => {
          if (field.isMapped && !field.isPlaceholder && !field.isVisual) {
            const value = getFieldValue(field.fieldName, !!field.isPerSite);
            return !value || value.trim() === '';
          }
          return false;
        });

        if (hasEmptyFields) {
          sectionsWithEmpty.add(sectionKey);
        }
      });
    }

    setSectionsWithEmptyFields(sectionsWithEmpty);
  }, [fieldsBySection, getFieldValue]);

  useEffect(() => {
    fetchCorrectionData();
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  useEffect(() => {
    checkSectionsWithEmptyFields();
  }, [checkSectionsWithEmptyFields]);

  const fetchCorrectionData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/correction/${invoiceId}?utilityType=${utilityType}`, {
        headers: { ...auth.getAuthHeaders() }
      });
      if (!response.ok) throw new Error('Failed to fetch correction data');
      
      const data = await response.json();
      setInvoiceData(data);
      setFieldsBySection(data.fieldsBySection);

      // Determine invoice shape and initialize state accordingly.
      const detectedShape: InvoiceShape = data.shape === 'supplier' ? 'supplier' : 'distribution';
      setShape(detectedShape);

      // Set initial active section to the first section after sorting by display_order
      if (data.fieldsBySection) {
        const sortedSections = Object.entries(data.fieldsBySection)
          .map(([key, section]: [string, any]) => ({
            key,
            display_order: section.display_order || 999
          }))
          .sort((a, b) => a.display_order - b.display_order);

        if (sortedSections.length > 0) {
          setActiveSection(sortedSections[0].key);
        }
      }

      if (detectedShape === 'supplier') {
        // Supplier flow: populate masterValues from data.master and siteValues from data.accounts.
        // fieldValues stays empty — supplier flow reads/writes via masterValues/siteValues.
        const masterInit: Record<string, string> = {};
        Object.entries(data.master || {}).forEach(([k, v]) => {
          masterInit[k] = v == null ? '' : String(v);
        });
        setMasterValues(masterInit);

        const sitesInit: Record<string, string>[] = (data.accounts || []).map((site: any) => {
          const siteRecord: Record<string, string> = {};
          Object.entries(site || {}).forEach(([k, v]) => {
            siteRecord[k] = v == null ? '' : String(v);
          });
          return siteRecord;
        });
        setSiteValues(sitesInit);
        setCurrentSiteIndex(0);
        setFieldValues({});
      } else {
        // Distribution flow: original behavior, unchanged.
        const initialValues: Record<string, string> = {};
        data.allFields.forEach((field: Field) => {
          initialValues[field.fieldName] = field.value;
        });
        setFieldValues(initialValues);
      }

      const pdfResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/upload/preview/${encodeURIComponent(data.s3Path)}`,
        { headers: { ...auth.getAuthHeaders() } }
      );
      if (pdfResponse.ok) {
        const blob = await pdfResponse.blob();
        setPdfBlobUrl(URL.createObjectURL(blob));
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching correction data:', error);
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName: string, value: string, isPerSite: boolean) => {
    if (shape === 'supplier') {
      if (isPerSite) {
        setSiteValues(prev => {
          const next = prev.slice();
          next[currentSiteIndex] = { ...(next[currentSiteIndex] || {}), [fieldName]: value };
          return next;
        });
      } else {
        setMasterValues(prev => ({ ...prev, [fieldName]: value }));
      }
    } else {
      setFieldValues(prev => ({
        ...prev,
        [fieldName]: value
      }));
    }

    // Auto-calculate fields if needed (distribution-only legacy behavior)
    if (shape === 'distribution' && (fieldName === 'Supply Charges 1' || fieldName === 'Supply Charges 2')) {
      calculateTotalSupplierCharges();
    }
  };

  const calculateTotalSupplierCharges = () => {
    const charge1 = parseFloat(fieldValues['Supply Charges 1'] || '0');
    const charge2 = parseFloat(fieldValues['Supply Charges 2'] || '0');
    const total = (charge1 + charge2).toFixed(2);
    
    setFieldValues(prev => ({
      ...prev,
      'Total Supplier Charges': total
    }));
  };

  // Assemble the corrected_data payload to send to the backend.
  // For supplier shape, mirror the extracted_data structure: master fields at top level
  // plus an accounts[] array (one entry per site). For distribution, send the flat object.
  const buildCorrectedData = useCallback((): any => {
    if (shape === 'supplier') {
      return {
        ...masterValues,
        accounts: siteValues,
      };
    }
    return fieldValues;
  }, [shape, masterValues, siteValues, fieldValues]);

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const newStatus = fromDuplicateQueue ? 'review' : invoiceData?.status;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/correction/${invoiceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...auth.getAuthHeaders() },
        body: JSON.stringify({
          correctedData: buildCorrectedData(),
          status: newStatus,
          currentS3Path: invoiceData?.s3Path,
          currentStatus: invoiceData?.status
        })
      });

      if (!response.ok) throw new Error('Failed to save draft');

      await response.json();

      if (fromDuplicateQueue && invoiceData) {
        setInvoiceData({ ...invoiceData, status: 'review' });
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setSaving(false);
    }
  };

  const checkEmptyFields = () => {
    let count = 0;
    if (!fieldsBySection) return count;

    Object.values(fieldsBySection).forEach(section => {
      section.fields.forEach(field => {
        if (!field.isMapped || field.isPlaceholder || field.isVisual) return;

        if (shape === 'supplier' && field.isPerSite) {
          // For per-site fields, count empties across ALL sites so the submit-time
          // warning reflects the full invoice (not just the currently-viewed site).
          siteValues.forEach(site => {
            const value = site?.[field.fieldName];
            if (!value || value.trim() === '') count++;
          });
        } else if (shape === 'supplier') {
          const value = masterValues[field.fieldName];
          if (!value || value.trim() === '') count++;
        } else {
          const value = fieldValues[field.fieldName];
          if (!value || value.trim() === '') count++;
        }
      });
    });

    return count;
  };

  const handleSubmitAndProcess = async () => {
    // Check if all mapped fields have values
    const emptyMappedFieldsCount = checkEmptyFields();
    
    // If there are mapped fields without values, show custom modal
    if (emptyMappedFieldsCount > 0) {
      setEmptyFieldsCount(emptyMappedFieldsCount);
      setShowValidationWarning(true);
      return;
    }
    
    proceedWithSubmit();
  };

  const proceedWithSubmit = async () => {
    setShowValidationWarning(false);
    setSavingFromEdit(false); // Reset the flag
    
    setSaving(true);
    try {
      const correctedPayload = buildCorrectedData();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/correction/${invoiceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...auth.getAuthHeaders() },
        body: JSON.stringify({
          correctedData: correctedPayload,
          status: 'success',
          currentS3Path: invoiceData?.s3Path,
          currentStatus: invoiceData?.status
        })
      });

      if (!response.ok) throw new Error('Failed to submit');

      await response.json();
      onSave(correctedPayload);
      onClose();
    } catch (error) {
      console.error('Error submitting:', error);
      // Error handled silently
    } finally {
      setSaving(false);
    }
  };

  const handleEditMode = () => {
    setIsEditMode(true);
  };

  const handleSaveFromEdit = async () => {
    // Check if all mapped fields have values
    const emptyMappedFieldsCount = checkEmptyFields();
    
    // If there are mapped fields without values, show custom modal
    if (emptyMappedFieldsCount > 0) {
      setEmptyFieldsCount(emptyMappedFieldsCount);
      setShowValidationWarning(true);
      // Set a flag to know we're saving from edit mode
      setSavingFromEdit(true);
      return;
    }
    
    proceedWithSaveFromEdit();
  };

  const proceedWithSaveFromEdit = async () => {
    setShowValidationWarning(false); // Close the validation modal
    setSavingFromEdit(false); // Reset the flag
    
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/correction/${invoiceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...auth.getAuthHeaders() },
        body: JSON.stringify({
          correctedData: buildCorrectedData(),
          status: invoiceData?.status,
          currentS3Path: invoiceData?.s3Path,
          currentStatus: invoiceData?.status
        })
      });

      if (!response.ok) throw new Error('Failed to save');
      
      await response.json();
      setIsEditMode(false); // Exit edit mode after successful save
      // Changes saved successfully - no alert needed
    } catch (error) {
      console.error('Error saving:', error);
      // Error handled silently
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm('Are you sure you want to reject this invoice? This action cannot be undone.')) {
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/correction/${invoiceId}/reject`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...auth.getAuthHeaders(),
        },
        body: JSON.stringify({
          correctedBy: 'user'
        })
      });

      if (!response.ok) throw new Error('Failed to reject invoice');

      await response.json();
      alert('Extracted invoice changed to rejected status');
      onSave({});
      onClose();
    } catch (error) {
      console.error('Error rejecting invoice:', error);
      alert('Failed to reject invoice. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field: Field, sectionIsPerSite: boolean) => {
    const isStartOfRepeatingSection =
      field.fieldName === 'Supplier Name 2' ||
      field.fieldName === 'Meter Number 2' ||
      field.fieldName === 'Meter Number 3' ||
      field.fieldName === 'Meter Number 4';

    const fieldValue = getFieldValue(field.fieldName, sectionIsPerSite);
    const isEmpty = field.isMapped && !field.isPlaceholder && !field.isVisual && (!fieldValue || fieldValue.trim() === '');

    return (
      <React.Fragment key={field.fieldName}>
        {isStartOfRepeatingSection && (
          <div className="field-separator" />
        )}

        <div className="field-value-box">
          {field.displayName}
        </div>

        <div className="field-value-box">
          {field.mapping || '-'}
        </div>

        <input
          type="text"
          value={fieldValue}
          onChange={(e) => handleFieldChange(field.fieldName, e.target.value, sectionIsPerSite)}
          placeholder={field.isPlaceholder ? '-' : ''}
          className={`field-value-input ${isEmpty ? 'field-empty' : ''}`}
          disabled={invoiceData?.status === 'success' && !isEditMode}
        />
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <div className="correction-modal">
        <div className="modal-overlay" onClick={onClose}></div>
        <div className="modal-content">
          <div className="loading">Loading correction data...</div>
        </div>
      </div>
    );
  }

  if (!invoiceData) {
    return (
      <div className="correction-modal">
        <div className="modal-overlay" onClick={onClose}></div>
        <div className="modal-content">
          <div className="error">Failed to load invoice data</div>
        </div>
      </div>
    );
  }

  // Use sections directly from database, sorted by display_order
  const sectionConfig = fieldsBySection ? 
    Object.entries(fieldsBySection)
      .map(([key, data]: [string, any]) => ({
        key,
        label: data.label,
        data,
        displayOrder: data.display_order || 999
      }))
      .sort((a, b) => a.displayOrder - b.displayOrder)
      : [];

  return (
    <div className="correction-modal">
      <div className="modal-overlay" onClick={onClose}></div>
      <div className="modal-content">
        <div className="modal-header">
          <h2>Invoice Correction - {invoiceData.fileName}</h2>
          <div className="header-info">
            <span className="client-info">{invoiceData.client.name} - {invoiceData.property.name}</span>
            <span className="provider-info">{invoiceData.providerName} ({invoiceData.utilityType})</span>
            {isAdjustment && (
              <span
                className="status-badge adjustment"
                title="Adjustment-only bill: a retroactive credit with no distribution charges. Supply Charges = Total Current Charges, distribution and billable usage are cleared, and the account carries an adj- prefix."
              >
                Adjustment
              </span>
            )}
            <span className={`status-badge ${invoiceData.status}`}>{invoiceData.status}</span>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="content-container">
            {/* Left side - PDF Viewer */}
            {showPdf && (
              <div className="pdf-viewer">
                <div className="pdf-header">
                  <h3>Original Invoice</h3>
                  <button onClick={() => setShowPdf(false)}>Hide PDF</button>
                </div>
                {pdfBlobUrl ? (
                  <iframe
                    src={pdfBlobUrl}
                    title="Invoice PDF"
                    className="pdf-iframe"
                  />
                ) : (
                  <div className="pdf-iframe" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Loading PDF...
                  </div>
                )}
              </div>
            )}

            {/* Right side - Form Fields */}
            <div className={`form-container ${!showPdf ? 'full-width' : ''}`}>
              {!showPdf && (
                <button className="show-pdf-button" onClick={() => setShowPdf(true)}>
                  Show PDF
                </button>
              )}

              {/* Section Tabs - 3x2 grid layout */}
              <div className="section-tabs-grid">
                {sectionConfig.map(section => (
                  <button
                    key={section.key}
                    className={`section-tab ${activeSection === section.key ? 'active' : ''} ${sectionsWithEmptyFields.has(section.key) ? 'has-empty-fields' : ''}`}
                    onClick={() => setActiveSection(section.key)}
                  >
                    {section.label}
                    <span className="field-count">{section.data.fields.length}</span>
                  </button>
                ))}
              </div>

              {/* Pagination — supplier-mode only, shown when active section is per-site
                  and there are multiple sites. Single-site supplier (Electric) hides the
                  control. Distribution invoices never see this. */}
              {(() => {
                const activeSectionData = sectionConfig.find(s => s.key === activeSection)?.data;
                const showPagination = shape === 'supplier' && !!activeSectionData?.isPerSite && siteValues.length > 1;
                if (!showPagination) return null;
                const ldc = siteValues[currentSiteIndex]?.['Account No'] || '';
                return (
                  <div className="site-pagination">
                    <div className="site-pagination-spacer" />
                    <span className="site-pagination-pill">
                      LDC #{ldc ? ` ${ldc}` : ''}
                    </span>
                    <div className="site-pagination-arrows">
                      <button
                        className="site-pagination-arrow"
                        onClick={() => setCurrentSiteIndex(Math.max(0, currentSiteIndex - 1))}
                        disabled={currentSiteIndex === 0}
                        aria-label="Previous site"
                      >
                        ‹
                      </button>
                      <span className="site-pagination-count">
                        Site {currentSiteIndex + 1} of {siteValues.length}
                      </span>
                      <button
                        className="site-pagination-arrow"
                        onClick={() => setCurrentSiteIndex(Math.min(siteValues.length - 1, currentSiteIndex + 1))}
                        disabled={currentSiteIndex >= siteValues.length - 1}
                        aria-label="Next site"
                      >
                        ›
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Fields Container - 3 column grid */}
              <div className="fields-container">
                {/* Header Row */}
                <div className="fields-header-row">
                  <div className="header-label">Final Label</div>
                  <div className="header-label">Mapped PDF Label</div>
                  <div className="header-label">Value</div>
                </div>

                {/* Fields Grid — each field renders per its own isPerSite flag */}
                <div className="fields-grid">
                  {(() => {
                    const activeSectionData = sectionConfig.find(s => s.key === activeSection)?.data;
                    if (!activeSectionData) return null;
                    return activeSectionData.fields.map((field: Field) =>
                      renderField(field, !!field.isPerSite)
                    );
                  })()}
                </div>
              </div>

              {/* Field Statistics — counts reflect what the user is currently viewing
                  (master fields + current site's per-site fields when in supplier mode). */}
              {(() => {
                // Build the list of "currently visible" fields:
                //  - distribution: all fields (matches legacy)
                //  - supplier: master fields + per-site fields for the active site only
                let visibleFieldNames: string[] = [];
                let valueLookup: (fieldName: string, isPerSite: boolean) => string;

                if (shape === 'supplier' && fieldsBySection) {
                  Object.values(fieldsBySection).forEach(section => {
                    section.fields.forEach(f => {
                      if (f.isMapped && !f.isPlaceholder && !f.isVisual) {
                        visibleFieldNames.push(`${f.isPerSite ? 'p:' : 'm:'}${f.fieldName}`);
                      }
                    });
                  });
                  valueLookup = (name: string, perSite: boolean) =>
                    perSite ? (siteValues[currentSiteIndex]?.[name] ?? '') : (masterValues[name] ?? '');
                } else {
                  visibleFieldNames = (invoiceData.allFields || [])
                    .filter((f: Field) => f.isMapped && !f.isPlaceholder && !f.isVisual)
                    .map((f: Field) => `m:${f.fieldName}`);
                  valueLookup = (name: string) => fieldValues[name] ?? '';
                }

                const extracted = visibleFieldNames.filter(prefixed => {
                  const isPerSite = prefixed.startsWith('p:');
                  const name = prefixed.slice(2);
                  const v = valueLookup(name, isPerSite);
                  return v && v.trim() !== '';
                }).length;
                const total = visibleFieldNames.length;
                const notExtracted = total - extracted;

                return (
                  <div className="field-stats">
                    <div className="stat">
                      <span className="stat-label">Total Fields:</span>
                      <span className="stat-value">{total}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Extracted:</span>
                      <span className="stat-value mapped">{extracted}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Not Extracted:</span>
                      <span className="stat-value unmapped">{notExtracted}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Confidence:</span>
                      <span className={`stat-value ${
                        invoiceData.confidenceScore >= 0.8 ? 'mapped' :
                        invoiceData.confidenceScore >= 0.5 ? 'visual' :
                        'unmapped'
                      }`}>
                        {Math.round(invoiceData.confidenceScore * 100)}%
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <div className="footer-info">
            {invoiceData.hasProviderConfig ? (
              <span className="config-status success">✓ Provider configuration found</span>
            ) : (
              <span className="config-status warning">⚠ Using generic configuration</span>
            )}
          </div>
          <div className="footer-actions">
            {/* Reject button - leftmost, available for all queues */}
            <button
              className="btn-reject"
              onClick={handleReject}
              disabled={saving}
            >
              {saving ? 'Rejecting...' : 'Reject'}
            </button>

            <button className="btn-cancel" onClick={isEditMode ? () => setIsEditMode(false) : onClose}>
              Cancel
            </button>

            {/* Success queue - show Edit button when not in edit mode */}
            {invoiceData?.status === 'success' && !isEditMode && (
              <button
                className="btn-edit"
                onClick={handleEditMode}
              >
                Edit
              </button>
            )}

            {/* Success queue - show Save button when in edit mode */}
            {invoiceData?.status === 'success' && isEditMode && (
              <button
                className="btn-save"
                onClick={handleSaveFromEdit}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            )}

            {/* Review/Error/Duplicate queue - show Save Draft and Submit & Process */}
            {invoiceData?.status !== 'success' && (
              <>
                <button
                  className="btn-save-draft"
                  onClick={handleSaveDraft}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Draft'}
                </button>
                <button
                  className="btn-save"
                  onClick={handleSubmitAndProcess}
                  disabled={saving}
                >
                  {saving ? 'Processing...' : 'Submit & Process'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Custom Validation Warning Modal */}
      {showValidationWarning && (
        <ValidationWarningModal
          emptyFieldsCount={emptyFieldsCount}
          onConfirm={savingFromEdit ? proceedWithSaveFromEdit : proceedWithSubmit}
          onCancel={() => {
            setShowValidationWarning(false);
            setSavingFromEdit(false);
          }}
        />
      )}
    </div>
  );
};

export default CorrectionModal;